/**
 * Combined Accessibility Enhancements and Audits for Shopify Websites
 *
 * This script aims to:
 * 1. Audit and (as a last resort) provide fallbacks for common accessibility issues.
 * 2. Enhance user experience for assistive technologies, particularly for dynamic elements.
 *
 * IMPORTANT: While this script provides valuable audits and fallbacks,
 * the BEST way to fix accessibility issues is directly in your Shopify theme's
 * Liquid/HTML/CSS files and through proper content entry in the Shopify admin.
 *
 * Place this code in a single JavaScript file (e.g., assets/accessibility-enhancements.js)
 * and link it in your theme.liquid file just before the closing </body> tag.
 */

// --- GLOBAL CONFIGURATION ---
// Set to 'true' to enable console logs and warnings for debugging.
// Set to 'false' to disable them for a production environment.
const ENABLE_LOGS = false;

/**
 * Custom console log function that only logs if ENABLE_LOGS is true.
 * @param {...any} args - Arguments to pass to console.log.
 */
function log(...args) {
    if (ENABLE_LOGS) {
        console.log(...args);
    }
}

/**
 * Custom console warn function that only warns if ENABLE_LOGS is true.
 * @param {...any} args - Arguments to pass to console.warn.
 */
function warn(...args) {
    if (ENABLE_LOGS) {
        console.warn(...args);
    }
}

/**
 * Custom console error function that only shows errors if ENABLE_LOGS is true.
 * @param {...any} args - Arguments to pass to console.error.
 */
function error(...args) {
    if (ENABLE_LOGS) {
        console.error(...args);
    }
}


// --- GLOBAL UTILITIES / HELPERS ---

/**
 * Creates or retrieves a visually hidden ARIA live region for screen reader announcements.
 * @returns {HTMLElement} The live region element.
 */
function createLiveRegion() {
    let liveRegion = document.getElementById('shopify-announcement-live-region');
    if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'shopify-announcement-live-region';
        liveRegion.setAttribute('aria-live', 'polite'); // 'polite' waits for user to finish current task
        liveRegion.setAttribute('aria-atomic', 'true'); // Announce the entire content of the region
        liveRegion.classList.add('visually-hidden'); // Apply CSS to hide visually but keep accessible
        document.body.appendChild(liveRegion);
    }
    return liveRegion;
}

/**
 * Announces a message to screen readers using an ARIA live region.
 * @param {string} message - The message to announce.
 */
function announceToScreenReader(message) {
    const liveRegion = createLiveRegion();
    // Clear previous content and add new message to ensure announcement
    liveRegion.textContent = ''; // Clear first to ensure new content is detected
    setTimeout(() => { // Small delay to ensure content change is registered by screen readers
        liveRegion.textContent = message;
    }, 100);
}

let lastFocusedElement = null; // To restore focus when modal closes

/**
 * Traps focus within a given container element.
 * @param {HTMLElement} container - The element to trap focus within.
 */
function trapFocus(container) {
    const focusableElements = container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]:not([disabled])), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (firstFocusable) {
        setTimeout(() => {
            firstFocusable.focus(); // Give focus to the first interactive element
        }, 100); // Small delay to ensure the modal is fully rendered
    }

    // Add keyboard listener for focus trapping (Tab/Shift+Tab)
    container.removeEventListener('keydown', handleFocusTrap); // Remove any old listener
    container.addEventListener('keydown', handleFocusTrap);

    function handleFocusTrap(event) {
        const isTabPressed = (event.key === 'Tab' || event.keyCode === 9);

        if (!isTabPressed) {
            return;
        }

        if (event.shiftKey) { // Shift + Tab
            if (document.activeElement === firstFocusable) {
                lastFocusable.focus();
                event.preventDefault();
            }
        } else { // Tab
            if (document.activeElement === lastFocusable) {
                firstFocusable.focus();
                event.preventDefault();
            }
        }
    }
}

/**
 * Handles the Escape key press for closing modals/overlays.
 * @param {HTMLElement} container - The container element.
 * @param {HTMLElement} closeButton - The button that closes the container.
 * @param {function} closeCallback - Optional callback function to execute on close.
 */
function handleEscapeKey(container, closeButton, closeCallback = null) {
    container.removeEventListener('keydown', escapeListener); // Remove any old listener
    container.addEventListener('keydown', escapeListener);

    function escapeListener(event) {
        if (event.key === 'Escape') {
            if (closeButton) {
                closeButton.click(); // Simulate click on the close button
            }
            if (closeCallback) {
                closeCallback();
            }
        }
    }
}

// --- 1. Unlabeled / Empty Links and Buttons Audit ---
/**
 * AUDIT SCRIPT: Checks for unlabeled/empty links and buttons.
 * Logs issues to the console and attempts to add fallbacks.
 */
function auditUnlabeledElements() {
    log("--- Auditing Unlabeled/Empty Links and Buttons ---");

    document.querySelectorAll('a, button, [role="button"]').forEach((element, index) => {
        const tagName = element.tagName.toLowerCase();
        const hasVisibleText = element.textContent.trim().length > 0;
        const hasAriaLabel = element.hasAttribute('aria-label') && element.getAttribute('aria-label').trim().length > 0;
        const hasImgWithAlt = element.querySelector('img[alt]') && element.querySelector('img[alt]').getAttribute('alt').trim().length > 0;
        const isSvgWithTitleOrAria = element.querySelector('svg') && (element.querySelector('svg title') || element.hasAttribute('aria-label'));

        // Check for truly empty/unlabeled elements
        if (!hasVisibleText && !hasAriaLabel && !hasImgWithAlt && !isSvgWithTitleOrAria) {
            warn(`Issue found: Unlabeled or empty ${tagName} #${index + 1}. Attempting to add fallback.`, element);

            // **MODIFIED: Add fallback logic for unlabeled elements**
            let fallbackLabel = inferLabelFromNearbyElements(element);

            if (fallbackLabel) {
                element.setAttribute('aria-label', fallbackLabel);
                log(`FIXED (JS Fallback): Added aria-label="${fallbackLabel}" to element #${index + 1}. **Review manually!**`, element);
            } else {
                error(`Unable to infer a meaningful label for element #${index + 1}. Manual fix required.`, element);
            }
        }

        // Check for non-descriptive links/buttons (e.g., "Click Here", "Read More")
        const nonDescriptiveKeywords = ['read more', 'click here', 'learn more', 'shop now', 'view product', 'go', 'view all'];
        const elementText = (element.textContent || '').toLowerCase();
        const ariaLabelText = (element.getAttribute('aria-label') || '').toLowerCase();

        const isNonDescriptive = (
            (hasVisibleText && nonDescriptiveKeywords.some(keyword => elementText.includes(keyword)) && elementText.length < 25) ||
            (hasAriaLabel && nonDescriptiveKeywords.some(keyword => ariaLabelText.includes(keyword)) && ariaLabelText.length < 25)
        );

        if (isNonDescriptive) {
            warn(`Potential issue: Non-descriptive text/aria-label for ${tagName} #${index + 1}. Review for better context.`, element);
        }
    });
    log("--- End Unlabeled/Empty Links and Buttons Audit ---");
}

/**
 * **NEW HELPER FUNCTION**
 * Infers a descriptive label from a nearby element.
 * @param {HTMLElement} element - The unlabeled element.
 * @returns {string|null} - The inferred label or null if none is found.
 */
function inferLabelFromNearbyElements(element) {
    // 1. Look for sibling headings (h1-h6) or labels
    const parent = element.parentElement;
    if (parent) {
        const siblings = Array.from(parent.children);
        const elementIndex = siblings.indexOf(element);

        // Check previous siblings
        for (let i = elementIndex - 1; i >= 0; i--) {
            const sibling = siblings[i];
            if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LABEL', 'P'].includes(sibling.tagName) && sibling.textContent.trim().length > 0) {
                return sibling.textContent.trim();
            }
        }

        // Check next siblings (in case of 'Read More' type links following a title)
        for (let i = elementIndex + 1; i < siblings.length; i++) {
            const sibling = siblings[i];
            if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LABEL', 'P'].includes(sibling.tagName) && sibling.textContent.trim().length > 0) {
                return sibling.textContent.trim();
            }
        }
    }

    // 2. Look for common data attributes
    if (element.hasAttribute('data-label')) {
        return element.getAttribute('data-label');
    }
    if (element.hasAttribute('data-title')) {
        return element.getAttribute('data-title');
    }
    
    // 3. Fallback to title attribute
    if (element.hasAttribute('title') && element.getAttribute('title').trim().length > 0) {
        return element.getAttribute('title');
    }

    return null;
}

// --- 2. Empty Headings and Logical Structure Audit ---
/**
 * AUDIT SCRIPT: Checks for empty headings and potentially illogical heading structure.
 * Logs issues to the console.
 * FIX: Manually remove or update empty tags in your theme's HTML/Liquid.
 * Ensure correct heading hierarchy (h1, then h2, then h3, etc.).
 */
function auditHeadings() {
    log("--- Auditing Headings ---");

    const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    let lastHeadingLevel = 0; // Tracks the level of the previously encountered heading

    headings.forEach((heading, index) => {
        const tagName = heading.tagName.toLowerCase();
        // Check for empty headings
        if (heading.textContent.trim().length === 0) {
            // New logic: Remove empty h2-h5 tags. Warn for h1.
            if (tagName !== 'h1') {
                warn(`Issue found: Empty heading tag <${tagName}> #${index + 1}. Removing from the DOM.`, heading);
                heading.remove();
                return; // Skip the rest of the loop for this heading as it's been removed
            } else {
                warn(`Issue found: Empty heading tag <${tagName}> #${index + 1}. This should be fixed manually as an empty H1 can affect SEO and document structure.`, heading);
            }
        }

        // Check for logical heading structure (basic check for skipped levels)
        const currentLevel = parseInt(tagName.charAt(1));
        if (lastHeadingLevel !== 0 && currentLevel > lastHeadingLevel + 1) {
            warn(`Potential issue: Heading level skipped. <${tagName}> #${index + 1} follows a <h${lastHeadingLevel}>. Consider using <h${lastHeadingLevel + 1}> for a logical structure.`, heading);
        }
        lastHeadingLevel = currentLevel;
    });
    log("--- End Headings Audit ---");
}

// --- 3. Accessible Form Fields Audit and Enhancement ---
/**
 * AUDIT/ENHANCEMENT SCRIPT: Checks and attempts to improve form field accessibility.
 * Prioritizes <label for="id"> association.
 * FIX: Primarily, ensure your Shopify theme's form snippets use <label for="id">.
 * This script is a fallback/enhancement for cases where labels might be missing.
 */
function improveFormAccessibility() {
    log("--- Improving Form Field Accessibility ---");

    document.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach((field, index) => {
        const fieldId = field.id;
        let hasAssociatedLabel = false;

        // 1. Check for <label for="id">
        if (fieldId) {
            const label = document.querySelector(`label[for="${fieldId}"]`);
            if (label && label.textContent.trim().length > 0) {
                hasAssociatedLabel = true;
            }
        }

        // 2. Check for wrapping <label>
        if (!hasAssociatedLabel) {
            let parentLabel = field.closest('label');
            if (parentLabel && parentLabel.textContent.trim().length > 0) {
                hasAssociatedLabel = true;
            }
        }

        // 3. Check for aria-label or aria-labelledby
        const hasAriaLabel = field.hasAttribute('aria-label') && field.getAttribute('aria-label').trim().length > 0;
        const hasAriaLabelledBy = field.hasAttribute('aria-labelledby') && document.getElementById(field.getAttribute('aria-labelledby'));

        if (!hasAssociatedLabel && !hasAriaLabel && !hasAriaLabelledBy) {
            warn(`Issue: Form field #${index + 1} (${field.name || field.id || field.type}) has no accessible label. Consider adding a <label> or aria-label.`, field);

            // *ATTEMPT TO ADD A BASIC ARIA-LABEL AS A FALLBACK*
            // This is a last resort and might not be perfect. Manual fix is always better.
            if (!field.hasAttribute('aria-label')) {
                let inferredLabel = field.placeholder || field.name;
                if (inferredLabel && inferredLabel.length > 0) {
                    field.setAttribute('aria-label', inferredLabel.replace(/[-_]/g, ' ')); // Convert name/placeholder to more readable label
                    log(`Added aria-label "${field.getAttribute('aria-label')}" to field #${index + 1}. Please verify.`, field);
                }
            }
        } else {
            // Field has a label
            // log(`Form field #${index + 1} (${field.name || field.id || field.type}) has an accessible label.`, field);
        }

        // Ensure required fields are marked programmatically
        if (field.hasAttribute('required') && !field.hasAttribute('aria-required')) {
            field.setAttribute('aria-required', 'true');
            log(`Added aria-required="true" to required field #${index + 1}.`, field);
        }
    });

    log("--- End Form Field Accessibility Improvement ---");
}

// --- 4. Improve Cart & Checkout Flow ---

/**
 * 4.1 "Add to Bag" must provide screen reader confirmation
 */
function enhanceAddToCartButton() {
    // Target the specific "Add to Bag" button using its data-cart-add attribute
    const addToCartButtons = document.querySelectorAll('button[data-cart-add]');

    addToCartButtons.forEach(button => {
        // Ensure no duplicate listeners are added
        button.removeEventListener('click', handleAddToCartClick);
        button.addEventListener('click', handleAddToCartClick);
    });

    // The 'cart:item:added' listener is left here as a best practice,
    // but may not be triggered by your theme's current 'minicartopen()' function.
    // If your theme dispatches a custom event *after* a successful AJAX add-to-cart,
    // this listener will capture it.
    document.addEventListener('cart:item:added', function(event) {
        const productTitle = event.detail.productTitle || event.detail.item?.product_title || 'An item';
        const quantity = event.detail.quantity || event.detail.item?.quantity || 1;
        announceToScreenReader(`${productTitle} added to bag. Quantity: ${quantity}.`);
        log(`Announced: ${productTitle} added to bag.`);
    });
}

function handleAddToCartClick(event) {
    // Announce immediately when the button is clicked.
    // The visual confirmation (minicart opening) and subsequent navigation by the user
    // should provide further context.
    const buttonText = event.currentTarget.querySelector('[data-add-to-cart-text]')?.textContent.trim() || 'Item';
    announceToScreenReader(`${buttonText} clicked. Opening bag...`);
    // log(`Announced: ${buttonText} clicked. Opening bag...`);
}

/**
 * 4.2 Cart pop-up must receive focus and be fully navigable via keyboard
 */
function enhanceCartPopUp() {
    // Target the specific minicart container
    const cartPopUp = document.querySelector('.minicart__container[data-cart-view]');

    if (!cartPopUp) {
        // log("Cart pop-up element not found with common selectors.");
        return;
    }

    // Observe for the cart pop-up becoming visible or hidden.
    // Based on HTML, it's likely controlled by `display: none/block` or `visibility: hidden/visible`
    // via its `style` attribute, and also by `minicart--open` class.
    const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            const isNowOpen = cartPopUp.classList.contains('minicart--open') ||
                              (cartPopUp.style.display !== 'none' && cartPopUp.style.visibility !== 'hidden' && cartPopUp.style.opacity !== '0');

            const wasOpen = mutation.oldValue && (mutation.oldValue.includes('minicart--open') ||
                            mutation.oldValue.includes('display: block') || mutation.oldValue.includes('visibility: visible') || mutation.oldValue.includes('opacity: 1'));

            if (isNowOpen && !wasOpen) { // If it just became open
                handleCartPopUpOpen(cartPopUp);
            } else if (!isNowOpen && wasOpen) { // If it just became hidden
                handleCartPopUpClose(cartPopUp);
            }
        });
    });

    observer.observe(cartPopUp, {
        attributes: true,
        attributeFilter: ['style', 'class'], // Observe these attributes
        attributeOldValue: true // To compare old vs. new values
    });

    // Initial check in case modal is already open on page load (e.g., direct link to cart)
    if (cartPopUp.classList.contains('minicart--open') || (cartPopUp.style.display !== 'none' && cartPopUp.style.visibility !== 'hidden' && cartPopUp.style.opacity !== '0')) {
        handleCartPopUpOpen(cartPopUp);
    }
}

function handleCartPopUpOpen(popUp) {
    // Check if pop-up is truly active
    if (popUp.classList.contains('minicart--open') || (popUp.style.display !== 'none' && popUp.style.visibility !== 'hidden' && popUp.style.opacity !== '0')) {
        log("Cart pop-up opened, applying accessibility enhancements.");
        lastFocusedElement = document.activeElement; // Store element that had focus before opening

        // 1. Set ARIA attributes for modal if not present
        if (!popUp.hasAttribute('role')) popUp.setAttribute('role', 'dialog');
        if (!popUp.hasAttribute('aria-modal')) popUp.setAttribute('aria-modal', 'true');

        // The provided HTML doesn't show a direct Hx title for the minicart,
        // so we'll rely on a generic aria-label for the dialog itself.
        if (!popUp.hasAttribute('aria-label') && !popUp.hasAttribute('aria-labelledby')) {
            popUp.setAttribute('aria-label', 'Shopping Cart');
        }

        // 2. Focus trapping
        trapFocus(popUp);

        // 3. Add a listener for the Escape key to close the modal
        const closeButton = popUp.querySelector('.minicart__close-button');
        handleEscapeKey(popUp, closeButton);
    }
}

function handleCartPopUpClose(popUp) {
    log("Cart pop-up closed, restoring focus.");
    popUp.removeEventListener('keydown', handleFocusTrap); // Remove event listener for focus trap
    popUp.removeEventListener('keydown', handleEscapeKey); // Remove event listener for escape key
    if (lastFocusedElement) {
        setTimeout(() => { // Small delay to ensure modal is fully closed before focus restore
            lastFocusedElement.focus(); // Restore focus to the element that opened the pop-up
            lastFocusedElement = null;
        }, 100);
    }
}

/**
 * 4.3 Ensure all buttons (quantity, remove, checkout) in cart are accessible
 */
function enhanceCartElements() {
    // 1. Quantity buttons (plus/minus)
    // Selectors: div.minus, div.plus within the .plus-minus-selector
    document.querySelectorAll('.plus-minus-selector .minus, .plus-minus-selector .plus').forEach(button => {
        // Ensure it's a div and acts like a button
        if (button.tagName.toLowerCase() === 'div' && !button.hasAttribute('role')) {
            button.setAttribute('role', 'button');
            button.setAttribute('tabindex', '0'); // Make it keyboard focusable
        }

        if (!button.hasAttribute('aria-label')) {
            const changeType = button.classList.contains('plus') ? 'plus' : 'minus';
            const itemContainer = button.closest('.cart__table__row'); // The row contains the item info
            let itemName = 'item';
            if (itemContainer) {
                const titleElement = itemContainer.querySelector('.product__title');
                if (titleElement) {
                    itemName = titleElement.textContent.trim();
                }
            }
            const labelText = `${changeType === 'plus' ? 'Increase' : 'Decrease'} quantity for ${itemName}`;
            button.setAttribute('aria-label', labelText);
            log(`Added aria-label "${labelText}" to quantity button.`, button);
        }
    });

    // 2. Remove buttons
    // Selector: a[data-cart-remove] - existing aria-label="Remove" needs enhancement
    document.querySelectorAll('a[data-cart-remove]').forEach(link => {
        // The existing HTML provides data-item which is useful!
        const existingAriaLabel = link.getAttribute('aria-label');
        const itemName = link.getAttribute('data-item'); // Get item name from data-item attribute

        if (itemName && existingAriaLabel !== `Remove ${itemName}`) {
            const labelText = `Remove ${itemName}`;
            link.setAttribute('aria-label', labelText);
            log(`Updated aria-label "${labelText}" for remove link.`, link);
        } else if (!itemName && !existingAriaLabel) {
            // Fallback if data-item is missing and no aria-label exists
            link.setAttribute('aria-label', 'Remove item from cart');
            warn(`Added generic aria-label "Remove item from cart" for remove link (data-item missing).`, link);
        }
    });

    // 3. Checkout button
    // Selector: a[href="/checkout"] - it already has aria-label="Continue to Checkout"
    const checkoutButton = document.querySelector('a[href="/checkout"].btn--black'); // Ensure it's the black button
    if (checkoutButton && checkoutButton.hasAttribute('aria-label')) {
        // It already has a good aria-label, so no change needed unless you want to be more specific.
        // log(`Checkout button already has aria-label: "${checkoutButton.getAttribute('aria-label')}". No change needed.`);
    } else if (checkoutButton && !checkoutButton.hasAttribute('aria-label')) {
        // Fallback in case the label is removed in future.
        checkoutButton.setAttribute('aria-label', 'Continue to checkout');
        log(`Added aria-label "Continue to checkout" to checkout button.`, checkoutButton);
    }

    // "Continue Shopping" button in empty cart
    const emptyCartContinueButton = document.querySelector('.minicart__empty .btn.closecart');
    if (emptyCartContinueButton && !emptyCartContinueButton.hasAttribute('aria-label')) {
        emptyCartContinueButton.setAttribute('aria-label', 'Continue Shopping'); // Already has this from provided HTML, good.
    }
}

// --- 5. Empty Image Alt Text Audit and Fallback ---
/**
 * AUDIT SCRIPT: Checks for images with missing or empty 'alt' attributes.
 * Provides a LAST-RESORT fallback for alt text.
 * PRIMARY FIX: Manually add descriptive alt text in your Shopify admin or theme Liquid files.
 */
function auditAndFixImageAltText() {
    log("--- Auditing and Fixing Image Alt Text ---");

    document.querySelectorAll('img').forEach((img, index) => {
        const hasAltAttribute = img.hasAttribute('alt');
        const altText = img.getAttribute('alt') ? img.getAttribute('alt').trim() : '';

        // Case 1: Image has no 'alt' attribute at all OR an empty 'alt' attribute
        if (!hasAltAttribute || altText === '') {
            // Check if it's intentionally decorative (alt="").
            // Simple heuristic: tiny images, or images with specific decorative classes
            const isDecorativeCandidate = img.classList.contains('decorative-image') || (img.naturalWidth <= 10 && img.naturalHeight <= 10);

            if (!isDecorativeCandidate) {
                warn(`Issue found: Image #${index + 1} has a missing or empty 'alt' attribute. Attempting to add fallback.`, img);
                addFallbackAltText(img, index);
            } else {
                // log(`Info: Image #${index + 1} has an empty 'alt' attribute. It appears to be decorative.`, img);
            }
        }
        // Case 2: Image has alt text (good!)
        else {
            // log(`Success: Image #${index + 1} has alt text: "${altText}"`, img);
        }
    });
    log("--- End Image Alt Text Audit ---");
}

/**
 * **MODIFIED HELPER FUNCTION**
 * Helper function to add a fallback alt text.
 * This is a LAST RESORT. Manual input is always better.
 * It tries to infer alt text from a variety of sources.
 * @param {HTMLImageElement} img - The image element.
 * @param {number} index - The index of the image (for logging).
 */
function addFallbackAltText(img, index) {
    let newAltText = '';
    
    // 1. Try to infer from a nearby heading or paragraph.
    const parent = img.closest('a, .product-item, .collection-item, figure'); // Look up for common containers
    if (parent) {
        // Find the first heading or p tag inside the parent
        const nearbyText = parent.querySelector('h1, h2, h3, h4, h5, h6, p');
        if (nearbyText && nearbyText.textContent.trim().length > 0) {
            newAltText = nearbyText.textContent.trim();
        }
    }
    
    // 2. Try to infer from the 'title' attribute on the image itself
    if (!newAltText && img.hasAttribute('title')) {
        newAltText = img.getAttribute('title').trim();
    }
    
    // 3. Try to infer from the filename if available
    if (!newAltText && img.src) {
        const url = new URL(img.src);
        const filename = url.pathname.split('/').pop();
        if (filename) {
            newAltText = filename.replace(/[-_]/g, ' ').replace(/\.\w+$/, '').trim();
        }
    }
    
    // 4. Final fallback
    if (!newAltText) {
        newAltText = `Image ${index + 1}`; // Generic fallback
    }

    if (img.getAttribute('alt') === '' || img.getAttribute('alt') === null) {
        img.setAttribute('alt', newAltText);
        warn(`FIXED (JS Fallback): Added alt="${newAltText}" to image #${index + 1}. **Review manually!**`, img);
    }
}

// --- 6. Navigation and Header Enhancements ---

/**
 * Enhances the skip link to ensure focus lands correctly.
 */
function enhanceSkipLink() {
    const skipLink = document.querySelector('.skip-link');
    const mainContent = document.getElementById('MainContent');

    if (skipLink && mainContent) {
        // Ensure MainContent is focusable
        if (!mainContent.hasAttribute('tabindex')) {
            mainContent.setAttribute('tabindex', '-1');
        }

        skipLink.addEventListener('click', (event) => {
            event.preventDefault();
            mainContent.focus();
            // Announce that focus has moved to main content
            announceToScreenReader("Skipped to main content.");
        });
    }
}

/**
 * Enhances mobile menu accessibility.
 */
function enhanceMobileMenu() {
    const mobileMenuButton = document.querySelector('.menu-icon'); // The hamburger button
    const mobileMenu = document.getElementById('hamburgermenu'); // The menu container
    const mobileMenuCloseButton = mobileMenu ? mobileMenu.querySelector('.close-icon') : null; // Assuming a close icon within the menu
    const modalShadow = document.getElementById('modalshadow'); // The overlay

    if (!mobileMenuButton || !mobileMenu || !modalShadow) {
        // log("Mobile menu elements not fully found.");
        return;
    }

    // Observe changes to the mobile menu's class or display style
    const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            const isMenuOpen = mobileMenu.classList.contains('burgeropen') || mobileMenu.classList.contains('Nav---open');
            const wasMenuOpen = mutation.oldValue && (mutation.oldValue.includes('burgeropen') || mutation.oldValue.includes('Nav---open'));

            if (isMenuOpen && !wasMenuOpen) {
                handleMobileMenuOpen(mobileMenu);
            } else if (!isMenuOpen && wasOpen) {
                handleMobileMenuClose(mobileMenu);
            }
        });
    });

    observer.observe(mobileMenu, {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true
    });

    // Also observe modalshadow's style as it indicates menu visibility
    const shadowObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            if (mutation.attributeName === 'style') {
                const isShadowVisible = modalShadow.style.display !== 'none';
                const wasShadowVisible = mutation.oldValue && mutation.oldValue.includes('display: block'); // Assuming block for visible

                if (isShadowVisible && !wasShadowVisible) {
                    handleMobileMenuOpen(mobileMenu); // Trigger open handling if shadow appears
                } else if (!isShadowVisible && wasShadowVisible) {
                    handleMobileMenuClose(mobileMenu); // Trigger close handling if shadow disappears
                }
            }
        });
    });
    shadowObserver.observe(modalShadow, {
        attributes: true,
        attributeFilter: ['style'],
        attributeOldValue: true
    });


    // Ensure mobile menu button has correct ARIA attributes
    if (!mobileMenuButton.hasAttribute('aria-expanded')) {
        mobileMenuButton.setAttribute('aria-expanded', 'false');
    }
    if (!mobileMenuButton.hasAttribute('aria-controls')) {
        mobileMenuButton.setAttribute('aria-controls', 'hamburgermenu');
    }
    if (!mobileMenuButton.hasAttribute('aria-label')) {
        mobileMenuButton.setAttribute('aria-label', 'Open mobile menu'); // Fallback if no label
    }
}

function handleMobileMenuOpen(menu) {
    log("Mobile menu opened, applying accessibility enhancements.");
    lastFocusedElement = document.activeElement; // Store focus

    if (!menu.hasAttribute('role')) menu.setAttribute('role', 'dialog'); // Can be 'dialog' or 'menu'
    if (!menu.hasAttribute('aria-modal')) menu.setAttribute('aria-modal', 'true');
    if (!menu.hasAttribute('aria-label') && !menu.hasAttribute('aria-labelledby')) {
        menu.setAttribute('aria-label', 'Mobile Navigation');
    }

    // Update the button that opened it
    const mobileMenuButton = document.querySelector('.menu-icon');
    if (mobileMenuButton) mobileMenuButton.setAttribute('aria-expanded', 'true');

    // Focus trapping
    trapFocus(menu);

    // Escape key to close
    const closeButton = menu.querySelector('.close-icon'); // Assuming .close-icon within menu
    handleEscapeKey(menu, closeButton);
}

function handleMobileMenuClose(menu) {
    log("Mobile menu closed, restoring focus.");
    menu.removeEventListener('keydown', handleFocusTrap);
    menu.removeEventListener('keydown', handleEscapeKey);

    const mobileMenuButton = document.querySelector('.menu-icon');
    if (mobileMenuButton) mobileMenuButton.setAttribute('aria-expanded', 'false');

    if (lastFocusedElement) {
        setTimeout(() => {
            lastFocusedElement.focus();
            lastFocusedElement = null;
        }, 100);
    }
}

/**
 * Enhances search input accessibility.
 */
function enhanceSearch() {
    const searchInput = document.querySelector('.site-search__input');
    if (searchInput && !searchInput.hasAttribute('aria-label')) {
        searchInput.setAttribute('aria-label', 'Search store'); // Fallback if missing
        log("Added aria-label 'Search store' to search input.");
    }

    // If there's a search toggle button
    const searchToggleButton = document.querySelector('.search-toggle'); // Common class for search icon button
    if (searchToggleButton && !searchToggleButton.hasAttribute('aria-label')) {
        searchToggleButton.setAttribute('aria-label', 'Open search');
        log("Added aria-label 'Open search' to search toggle.");
    }
}


// --- 7. Product Page Enhancements ---

/**
 * Enhances product page variant selectors (if they are custom elements, not native <select>).
 */
function enhanceProductVariantSelectors() {
    // This targets common Shopify custom variant selectors. Adjust selectors as needed.
    // Assuming variant options are buttons or divs with a specific role
    document.querySelectorAll('.single-option-selector__radio, .product-form__input input[type="radio"], .variant-swatch').forEach(variantInput => {
        // Ensure proper linking to labels
        const id = variantInput.id;
        if (id) {
            const label = document.querySelector(`label[for="${id}"]`);
            if (label) {
                // If label exists, it's good. Ensure custom radios have role="radio" and aria-checked
                if (variantInput.type === 'radio' || variantInput.type === 'checkbox') {
                    if (!variantInput.hasAttribute('role')) {
                        variantInput.setAttribute('role', variantInput.type);
                    }
                    // For custom selectors that use divs/buttons instead of native inputs:
                } else if (!variantInput.hasAttribute('role')) {
                    variantInput.setAttribute('role', 'option'); // For a listbox option
                }

                // If a non-input element is acting as a selector
                if (variantInput.tagName.toLowerCase() !== 'input' && !variantInput.hasAttribute('tabindex')) {
                    variantInput.setAttribute('tabindex', '0'); // Make it focusable
                }
            } else {
                warn("Product variant input missing associated label:", variantInput);
                if (!variantInput.hasAttribute('aria-label')) {
                    let inferredLabel = variantInput.value || variantInput.dataset.optionValue || variantInput.dataset.variantName;
                    if (inferredLabel) {
                        variantInput.setAttribute('aria-label', inferredLabel);
                        log(`Added aria-label "${inferredLabel}" to product variant input.`, variantInput);
                    }
                }
            }
        }
    });

    // Ensure main variant wrapper has role="radiogroup" if applicable
    document.querySelectorAll('.selector-wrapper, .product-form__controls-group').forEach(group => {
        if (group.querySelector('input[type="radio"]') && !group.hasAttribute('role')) {
            group.setAttribute('role', 'radiogroup');
            // Look for a label for the group, e.g., "Size", "Color"
            const groupLabel = group.querySelector('.product-form__controls-label') || group.previousElementSibling; // Common patterns
            if (groupLabel && groupLabel.tagName.toLowerCase() === 'label' && groupLabel.id) {
                group.setAttribute('aria-labelledby', groupLabel.id);
            } else if (groupLabel && groupLabel.textContent.trim().length > 0 && !group.hasAttribute('aria-label')) {
                group.setAttribute('aria-label', groupLabel.textContent.trim());
            } else if (!group.hasAttribute('aria-label')) {
                // Fallback for generic label for the group
                group.setAttribute('aria-label', 'Product Options');
            }
        }
    });

    // Product Quantity input on product page
    const productQuantityInput = document.getElementById('Quantity'); // Common ID for product page quantity
    if (productQuantityInput) {
        if (!productQuantityInput.hasAttribute('aria-label')) {
            productQuantityInput.setAttribute('aria-label', 'Product quantity');
            log("Added aria-label 'Product quantity' to product quantity input.");
        }
        // Ensure its associated plus/minus buttons are accessible
        const quantityContainer = productQuantityInput.closest('.product-form__controls-group'); // Or similar parent
        if (quantityContainer) {
            const minusButton = quantityContainer.querySelector('[name="minus"]'); // Or similar selector
            const plusButton = quantityContainer.querySelector('[name="plus"]'); // Or similar selector

            if (minusButton && !minusButton.hasAttribute('aria-label')) {
                minusButton.setAttribute('aria-label', 'Decrease quantity');
                if (minusButton.tagName.toLowerCase() !== 'button') minusButton.setAttribute('role', 'button');
                if (minusButton.tagName.toLowerCase() !== 'button' && !minusButton.hasAttribute('tabindex')) minusButton.setAttribute('tabindex', '0');
            }
            if (plusButton && !plusButton.hasAttribute('aria-label')) {
                plusButton.setAttribute('aria-label', 'Increase quantity');
                if (plusButton.tagName.toLowerCase() !== 'button') plusButton.setAttribute('role', 'button');
                if (plusButton.tagName.toLowerCase() !== 'button' && !plusButton.hasAttribute('tabindex')) plusButton.setAttribute('tabindex', '0');
            }
        }
    }
}

// --- 8. Carousel/Slider Enhancements ---
/**
 * Enhances Splide.js carousels for accessibility.
 * Assumes Splide.js is used based on common Shopify themes.
 * Adjust selectors if a different slider library is in use.
 */
function enhanceCarousels() {
    document.querySelectorAll('.splide').forEach((carouselElement, index) => {
        // Ensure the carousel container is accessible
        if (!carouselElement.hasAttribute('role')) {
            carouselElement.setAttribute('role', 'region');
            carouselElement.setAttribute('aria-label', `Image carousel ${index + 1}`);
        }

        // Navigation arrows
        carouselElement.querySelectorAll('.splide__arrow').forEach(arrow => {
            if (!arrow.hasAttribute('aria-label')) {
                const isNext = arrow.classList.contains('splide__arrow--next');
                arrow.setAttribute('aria-label', isNext ? 'Next slide' : 'Previous slide');
            }
        });

        // Pagination dots
        const paginationList = carouselElement.querySelector('.splide__pagination');
        if (paginationList && !paginationList.hasAttribute('aria-label')) {
            paginationList.setAttribute('aria-label', 'Slide navigation');
        }

        carouselElement.querySelectorAll('.splide__pagination__page').forEach((dot, dotIndex) => {
            if (!dot.hasAttribute('aria-label')) {
                dot.setAttribute('aria-label', `Go to slide ${dotIndex + 1}`);
            }
            // Ensure dots are buttons for proper semantics
            if (dot.tagName.toLowerCase() !== 'button') {
                dot.setAttribute('role', 'button');
                dot.setAttribute('tabindex', '0');
            }
        });

        // Slides themselves
        carouselElement.querySelectorAll('.splide__slide').forEach((slide, slideIndex) => {
            if (!slide.hasAttribute('role')) {
                slide.setAttribute('role', 'group'); // For each slide within a region
                slide.setAttribute('aria-roledescription', 'slide');
                slide.setAttribute('aria-label', `Slide ${slideIndex + 1} of ${carouselElement.querySelectorAll('.splide__slide').length}`);
            }
            // Ensure images within slides have alt text (handled by auditAndFixImageAltText but good to re-emphasize)
            slide.querySelectorAll('img').forEach(img => {
                if (!img.hasAttribute('alt') || img.getAttribute('alt').trim() === '') {
                    // Fallback to title of the image or a generic label if no specific content is present.
                    let inferredAlt = img.title || `Image for slide ${slideIndex + 1}`;
                    img.setAttribute('alt', inferredAlt);
                    warn(`Added fallback alt text for image in carousel slide: "${inferredAlt}"`, img);
                }
            });
        });
    });
}

// --- 9. Email Pop-up Enhancements ---

/**
 * Enhances email signup pop-up accessibility.
 */
function enhanceEmailPopup() {
    const emailPopup = document.querySelector('.email-popup');
    const closeButton = emailPopup ? emailPopup.querySelector('.close-icon') || emailPopup.querySelector('.close-button') : null; // Common close selectors

    if (!emailPopup) return;

    const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            if (mutation.attributeName === 'style') {
                const isPopupVisible = emailPopup.style.display !== 'none';
                const wasPopupVisible = mutation.oldValue && mutation.oldValue.includes('display: block');

                if (isPopupVisible && !wasPopupVisible) {
                    handleEmailPopupOpen(emailPopup, closeButton);
                } else if (!isPopupVisible && wasPopupVisible) {
                    handleEmailPopupClose(emailPopup);
                }
            }
        });
    });

    observer.observe(emailPopup, {
        attributes: true,
        attributeFilter: ['style'],
        attributeOldValue: true
    });

    // Initial check if popup is already visible
    if (emailPopup.style.display !== 'none') {
        handleEmailPopupOpen(emailPopup, closeButton);
    }
}

function handleEmailPopupOpen(popup, closeButton) {
    log("Email popup opened, applying accessibility enhancements.");
    lastFocusedElement = document.activeElement;

    if (!popup.hasAttribute('role')) popup.setAttribute('role', 'dialog');
    if (!popup.hasAttribute('aria-modal')) popup.setAttribute('aria-modal', 'true');
    if (!popup.hasAttribute('aria-label') && !popup.hasAttribute('aria-labelledby')) {
        popup.setAttribute('aria-label', 'Email Signup Pop-up');
    }

    trapFocus(popup);

    if (closeButton && !closeButton.hasAttribute('aria-label')) {
        closeButton.setAttribute('aria-label', 'Close email signup popup');
    }
    handleEscapeKey(popup, closeButton);
}

function handleEmailPopupClose(popup) {
    log("Email popup closed, restoring focus.");
    popup.removeEventListener('keydown', handleFocusTrap);
    popup.removeEventListener('keydown', handleEscapeKey);
    if (lastFocusedElement) {
        setTimeout(() => {
            lastFocusedElement.focus();
            lastFocusedElement = null;
        }, 100);
    }
}


// --- MAIN INITIALIZATION & EVENT LISTENERS ---

document.addEventListener('DOMContentLoaded', () => {
    log("Accessibility script loaded and DOMContentLoaded.");
    auditUnlabeledElements();
    auditHeadings();
    improveFormAccessibility();
    enhanceAddToCartButton(); // Sets up add-to-cart confirmation
    enhanceCartElements();    // Ensures cart buttons are labeled (initial run)
    enhanceCartPopUp();       // Manages cart pop-up focus trapping and ARIA attributes
    auditAndFixImageAltText();
    enhanceSkipLink();        // Enhances skip to content link
    enhanceMobileMenu();      // Enhances mobile menu accessibility
    enhanceSearch();          // Enhances search input accessibility
    enhanceProductVariantSelectors(); // Enhances product page variant selectors
    enhanceCarousels();       // Enhances carousel accessibility
    enhanceEmailPopup();      // Enhances email popup accessibility
});

// Listen for Shopify's section load event (common for AJAX updates)
// This ensures accessibility enhancements are re-applied to dynamically loaded content.
document.addEventListener('shopify:section:load', function(event) {
    log(`Shopify section loaded: ${event.detail.sectionId}. Re-running relevant audits/enhancements.`);
    // Re-run audits/enhancements that apply to dynamically loaded sections
    auditUnlabeledElements();
    auditHeadings();
    improveFormAccessibility();
    enhanceCartElements(); // Re-apply to potentially new/updated cart elements
    auditAndFixImageAltText(); // Re-apply to potentially new images in loaded sections
    enhanceSkipLink(); // Ensure skip link is functional if section reloads main content area
    enhanceMobileMenu(); // Re-check if mobile menu is affected by section loads
    enhanceSearch();
    enhanceProductVariantSelectors();
    enhanceCarousels();
    enhanceEmailPopup(); // Re-check if email popup is loaded dynamically

    // If the reloaded section contains the cart pop-up itself, ensure its focus trap is re-initialized
    const cartPopUp = document.querySelector('.minicart__container[data-cart-view]');
    if (cartPopUp && (cartPopUp.classList.contains('minicart--open') || (cartPopUp.style.display !== 'none' && cartPopUp.style.visibility !== 'hidden'))) {
        handleCartPopUpOpen(cartPopUp); // Re-initialize focus trap if already open
    }
});

// Listen for common cart update events if your theme dispatches them
// (e.g., after changing quantity, removing item)
document.addEventListener('cart:updated', function() {
    log("Cart updated event detected. Re-running cart element enhancements.");
    enhanceCartElements();
});